/**
 * Человекочитаемые ошибки загрузки РД архивом. Логика разбора — как у
 * `errorText` в `pages/Pto/kfo/ui.tsx` (`{error:{message}}` бэкенда, строка,
 * нет связи), плюс: снимаем служебный префикс бэкенда («conflict: …»), 403 —
 * «нет права записи», 413 — лимит файла. Здесь, а не в компоненте: хук
 * загрузки не должен зависеть от файлов разметки.
 */

import { httpStatus } from '@shared/lib/uploadBatches';

export { httpStatus, isAbortError } from '@shared/lib/uploadBatches';

export const NO_WRITE_ACCESS = 'Нет права записи в проект — попросите добавить вас в проект.';
export const NO_CONNECTION = 'Нет связи с сервером. Повторите позже.';

/** Служебные префиксы `AppError` бэкенда (`osnova-core/src/error.rs`). */
const PREFIX =
  /^(not found|conflict|validation|bad request|upstream error|service unavailable|database error|internal error):\s*/i;

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Сообщение сервера из тела ошибки (`{error:{message}}`, `{error:"…"}`, `{message}`). */
function serverMessage(err: unknown): string | null {
  const data = (err as { data?: unknown } | null | undefined)?.data as
    | { error?: { message?: string; code?: string } | string; message?: string }
    | string
    | undefined
    | null;
  if (!data) return null;
  if (typeof data === 'string') return data.trim() || null;
  if (typeof data.error === 'string' && data.error) return data.error;
  if (typeof data.error === 'object' && data.error?.message) return data.error.message;
  if (typeof data.message === 'string' && data.message) return data.message;
  return null;
}

/** Код ошибки бэкенда (`internal_error`, `conflict`, …), если есть. */
function serverCode(err: unknown): string | null {
  const data = (err as { data?: unknown } | null | undefined)?.data as
    { error?: { code?: string } } | undefined | null;
  return typeof data === 'object' && data && typeof data.error === 'object'
    ? (data.error?.code ?? null)
    : null;
}

/** Человекочитаемая причина ошибки API; `fallback` — если причины нет. */
export function errorText(err: unknown, fallback: string): string {
  const status = httpStatus(err);
  if (status === 'FETCH_ERROR') return NO_CONNECTION;
  if (status === 'TIMEOUT_ERROR') return 'Сервер не ответил вовремя. Повторите позже.';
  if (status === 401) return 'Сессия истекла — войдите в систему заново.';
  if (status === 403) return NO_WRITE_ACCESS;
  if (status === 413) return 'Файл больше 200 МБ — сервер такие не принимает.';
  if (status === 429) return 'Слишком много запросов — повторите через минуту.';
  const code = serverCode(err);
  const message = serverMessage(err);
  // Внутренние ошибки (текст SQL, сериализации) пользователю ничего не скажут.
  if (code === 'internal_error' || code === 'database_error') {
    return `${fallback} Внутренняя ошибка сервера — повторите позже или сообщите администратору.`;
  }
  if (message) return capitalize(message.replace(PREFIX, '').trim());
  if (typeof status === 'number' && status >= 500) {
    return `${fallback} Ошибка сервера (${status}) — повторите позже.`;
  }
  if (err instanceof Error && err.message) return capitalize(err.message);
  return fallback;
}

/** Причина, по которой файл пакета не ушёл на сервер (для списка «не загрузились»). */
export function uploadErrorText(err: unknown): string {
  const status = httpStatus(err);
  if (status === 413) return 'больше 200 МБ — сервер такие не принимает';
  if (status === 404) return 'пакет не найден — возможно, его удалили';
  if (status === 409) return 'пакет больше не принимает файлы — он уже отправлен на распознавание';
  const text = errorText(err, 'Не удалось отправить.');
  // «Нет связи…» → «нет связи…» (идёт после пути файла); аббревиатуры («РД …») не трогаем.
  return text.length > 1 && text[1] === text[1].toLowerCase()
    ? text.charAt(0).toLowerCase() + text.slice(1)
    : text;
}
